import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Breadcrumbs,
  CircularProgress,
  Button,
  Stack,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { formatDateTimeSecondsSk } from "app/utils/dateFormat";
import {
  getBillingCronRuns,
  getBillingCronRun,
  runBillingCycleNow,
  refreshBillingPayments,
  testBillingEmailDryRun,
  sendSelectedBillingRunEmails,
  resendFailedBillingRunEmails,
} from "app/services/api/billing";
import BillingBreadcrumbs from "./BillingBreadcrumbs";

const RESULT_STATUS_COLOR = {
  created: "success",
  skipped: "default",
  error: "error",
};

/** Zrozumiteľné hlášky problémov z dry-run testu odosielania. */
const DRYRUN_ISSUE_LABELS = {
  notif_not_configured: "Notifikačný server nie je nakonfigurovaný",
  no_billing_email: "Chýba fakturačný email",
  invalid_billing_email: "Neplatný fakturačný email",
  no_items: "Žiadne fakturačné položky",
  "notif:no_billing_email": "Notif: chýba príjemca (email)",
  "notif:no_template:email": "Notif: chýba šablóna emailu",
  "notif:unsupported_event_for_dryrun": "Notif: nepodporovaný event",
};
const dryRunIssueLabel = (code) => {
  if (DRYRUN_ISSUE_LABELS[code]) return DRYRUN_ISSUE_LABELS[code];
  if (code.startsWith("notif_error:"))
    return `Notif server: ${code.slice("notif_error:".length)}`;
  return code;
};

/** Suma v EUR (sk formát, 2 desatinné). */
const fmtEur = (n) =>
  `${(Number(n) || 0).toLocaleString("sk-SK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

/**
 * Stabilná identita výsledku behu (faktúry) pre výber checkboxom. Nezávisí od invoiceId
 * (to prichádza až z obohateného detailu) — použije customerId + invoiceNumber + periodKey,
 * čo backend vie dohľadať rovnako ako pri odosielaní.
 */
const resultKey = (r) =>
  `${r?.customerId ?? ""}::${r?.invoiceNumber ?? ""}::${r?.periodKey ?? ""}`;

/**
 * Vráti totals behu; ak chýba (staršie behy pred zavedením), dopočíta z results[].totals.
 * @returns {{net:number, vat:number, total:number, vatBreakdown:Array}}
 */
function getRunTotals(run) {
  if (run?.totals && typeof run.totals === "object") return run.totals;
  const round2 = (n) => Math.round(n * 100) / 100;
  const byRate = new Map();
  let net = 0;
  let vat = 0;
  (run?.results || []).forEach((r) => {
    const t = r.totals;
    if (!t) return;
    net += Number(t.net) || 0;
    vat += Number(t.vat) || 0;
    (t.vatBreakdown || []).forEach((v) => {
      const cur = byRate.get(v.rate) || { rate: v.rate, base: 0, vat: 0 };
      cur.base += Number(v.base) || 0;
      cur.vat += Number(v.vat) || 0;
      byRate.set(v.rate, cur);
    });
  });
  const vatBreakdown = [...byRate.values()]
    .map((x) => ({ rate: x.rate, base: round2(x.base), vat: round2(x.vat) }))
    .sort((a, b) => b.rate - a.rate);
  return {
    net: round2(net),
    vat: round2(vat),
    total: round2(net + vat),
    vatBreakdown,
  };
}

const BillingHistoryPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [syncingPayments, setSyncingPayments] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailRun, setDetailRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Vybrané faktúry v detaile behu (identita riadku, viď resultKey), na odoslanie emailom.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  // Reálne hromadné odoslanie emailov za beh.
  const [sendEmailsConfirm, setSendEmailsConfirm] = useState(null); // run alebo null
  const [sendingEmails, setSendingEmails] = useState(false);
  const [resendingFailed, setResendingFailed] = useState(false);

  const handleResendFailed = async (run) => {
    if (!run?._id) return;
    setResendingFailed(true);
    try {
      const res = await resendFailedBillingRunEmails(run._id);
      if (res?.success) {
        const s = res.data || {};
        enqueueSnackbar(
          t("pages.dsokBilling.history.resendDone", {
            defaultValue: `Doposlané — znova: ${s.resent ?? 0}, OK/preskočené: ${
              s.okAlready ?? 0
            }, chyby: ${s.failed ?? 0}`,
          }),
          { variant: s.failed > 0 ? "warning" : "success" },
        );
        setDetailRun(null);
        fetchRuns();
      } else {
        enqueueSnackbar(
          res?.reason ||
            t("pages.dsokBilling.history.resendError", {
              defaultValue: "Doposlanie zlyhalo",
            }),
          { variant: "error" },
        );
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.reason ||
          err?.message ||
          t("pages.dsokBilling.history.resendError", {
            defaultValue: "Doposlanie zlyhalo",
          }),
        { variant: "error" },
      );
    } finally {
      setResendingFailed(false);
    }
  };

  // Otvorí detail behu a dotiahne obohatené výsledky (invoiceId + príznak už-odoslané),
  // aby sa dali faktúry označovať a odoslať jednotlivo.
  const openDetail = async (run) => {
    setSelectedKeys(new Set());
    setDetailRun(run);
    if (!run?._id) return;
    setDetailLoading(true);
    try {
      const res = await getBillingCronRun(run._id);
      if (res?.success && res.data) {
        setDetailRun(res.data);
      }
    } catch (e) {
      // ostane základný detail bez obohatenia
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailRun(null);
    setSelectedKeys(new Set());
  };

  const refreshDetail = async (runId) => {
    if (!runId) return;
    try {
      const res = await getBillingCronRun(runId);
      if (res?.success && res.data) setDetailRun(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  // Riadok sa dá označiť, keď je faktúra vytvorená (created) a ešte NEbola odoslaná.
  // Príznak alreadySent prichádza z obohateného detailu; ak (ešte) nie je k dispozícii,
  // riadok ostáva označiteľný a backend už-odoslané aj tak preskočí.
  const isRowSelectable = (r) => r?.status === "created" && !r?.alreadySent;

  const selectableRows = (detailRun?.results || []).filter(isRowSelectable);
  const selectableKeys = selectableRows.map(resultKey);
  const allSelectableChecked =
    selectableKeys.length > 0 &&
    selectableKeys.every((k) => selectedKeys.has(k));
  const someSelectableChecked =
    selectableKeys.some((k) => selectedKeys.has(k)) && !allSelectableChecked;

  const toggleSelectAll = () => {
    setSelectedKeys(
      allSelectableChecked ? new Set() : new Set(selectableKeys),
    );
  };

  const toggleRow = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const doSendSelected = async (run) => {
    if (!run?._id) return;
    // Preveď vybrané kľúče na identitu výsledkov, ktorú backend vie dohľadať.
    const selections = (detailRun?.results || [])
      .filter((r) => isRowSelectable(r) && selectedKeys.has(resultKey(r)))
      .map((r) => ({
        customerId: r.customerId,
        invoiceNumber: r.invoiceNumber || null,
        periodKey: r.periodKey || null,
      }));
    setSendEmailsConfirm(null);
    if (selections.length === 0) return;
    setSendingEmails(true);
    try {
      const res = await sendSelectedBillingRunEmails(run._id, selections);
      if (res?.success) {
        const s = res.data || {};
        enqueueSnackbar(
          t("pages.dsokBilling.history.sendEmailsDone", {
            defaultValue: `Odoslané — úspešne: ${s.sent ?? 0}, preskočené: ${
              s.skipped ?? 0
            }, chyby: ${s.failed ?? 0}`,
          }),
          { variant: s.failed > 0 ? "warning" : "success" },
        );
        setSelectedKeys(new Set());
        await refreshDetail(run._id);
        fetchRuns();
      } else {
        enqueueSnackbar(
          res?.reason ||
            t("pages.dsokBilling.history.sendEmailsError", {
              defaultValue: "Odoslanie emailov zlyhalo",
            }),
          { variant: "error" },
        );
      }
    } catch (err) {
      const reason = err?.response?.data?.reason;
      const map = {
        no_invoices_selected: "Nie je označená žiadna faktúra.",
        email_disabled: "Odosielanie je vypnuté (master flag).",
        invoice_sending_disabled:
          "Odosielanie faktúr je vypnuté v systémových nastaveniach.",
        notification_not_configured:
          "Notifikačný server nie je nakonfigurovaný.",
      };
      enqueueSnackbar(
        map[reason] ||
          err?.message ||
          t("pages.dsokBilling.history.sendEmailsError", {
            defaultValue: "Odoslanie emailov zlyhalo",
          }),
        { variant: "error" },
      );
    } finally {
      setSendingEmails(false);
    }
  };

  const handleDryRunTest = async () => {
    setDryRunning(true);
    try {
      const res = await testBillingEmailDryRun();
      if (res?.success) {
        setDryRunResult(res.data);
      } else {
        enqueueSnackbar(
          res?.reason ||
            t("pages.dsokBilling.history.dryRunError", {
              defaultValue: "Test odoslania zlyhal",
            }),
          { variant: "error" },
        );
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.reason ||
          err?.message ||
          t("pages.dsokBilling.history.dryRunError", {
            defaultValue: "Test odoslania zlyhal",
          }),
        { variant: "error" },
      );
    } finally {
      setDryRunning(false);
    }
  };

  const handleRefreshPayments = async () => {
    setSyncingPayments(true);
    try {
      const res = await refreshBillingPayments();
      if (res?.success) {
        const s = res.data || {};
        enqueueSnackbar(
          t("pages.dsokBilling.history.paymentsDone", {
            defaultValue: `Stavy platieb aktualizované — skontrolované: ${
              s.checked ?? 0
            }, zaplatené: ${s.paid ?? 0}, chyby: ${s.errors ?? 0}`,
          }),
          { variant: s.errors > 0 ? "warning" : "success" },
        );
      } else if (res?.reason === "already_running") {
        enqueueSnackbar(
          t("pages.dsokBilling.history.paymentsRunning", {
            defaultValue: "Import platieb už beží, skús neskôr.",
          }),
          { variant: "info" },
        );
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.reason ||
          err?.message ||
          t("pages.dsokBilling.history.paymentsError", {
            defaultValue: "Import stavov platieb zlyhal",
          }),
        { variant: "error" },
      );
    } finally {
      setSyncingPayments(false);
    }
  };

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBillingCronRuns(100);
      setRuns(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(
        err?.response?.data?.reason ||
          err?.message ||
          t("pages.dsokBilling.history.fetchError", {
            defaultValue: "Načítanie behov zlyhalo",
          }),
      );
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const handleRunNow = async () => {
    setConfirmOpen(false);
    setRunning(true);
    try {
      const res = await runBillingCycleNow();
      if (res?.success) {
        const s = res.data || {};
        enqueueSnackbar(
          t("pages.dsokBilling.history.runDone", {
            defaultValue: `Hotovo — vytvorené: ${s.created ?? 0}, preskočené: ${
              s.skipped ?? 0
            }, chyby: ${s.errors ?? 0}`,
          }),
          { variant: s.errors > 0 ? "warning" : "success" },
        );
      } else if (res?.reason === "already_running") {
        enqueueSnackbar(
          t("pages.dsokBilling.history.alreadyRunning", {
            defaultValue: "Cyklus už beží, skús neskôr.",
          }),
          { variant: "info" },
        );
      }
      fetchRuns();
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.reason ||
          err?.message ||
          t("pages.dsokBilling.history.runError", {
            defaultValue: "Spustenie zlyhalo",
          }),
        { variant: "error" },
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <BillingBreadcrumbs active="history" />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
        >
          <Typography variant="h2">
            {t("pages.dsokBilling.history.title", {
              defaultValue: "História faktúrácie",
            })}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchRuns}
              disabled={loading || running}
            >
              {t("pages.dsokBilling.history.refresh", {
                defaultValue: "Obnoviť",
              })}
            </Button>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={
                syncingPayments ? <CircularProgress size={16} /> : <PaidOutlinedIcon />
              }
              onClick={handleRefreshPayments}
              disabled={syncingPayments}
            >
              {t("pages.dsokBilling.history.refreshPayments", {
                defaultValue: "Aktualizovať stav z Abry",
              })}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={dryRunning ? <CircularProgress size={16} /> : null}
              onClick={handleDryRunTest}
              disabled={dryRunning}
            >
              {t("pages.dsokBilling.history.dryRunTest", {
                defaultValue: "Test odoslania (dry-run)",
              })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={
                running ? <CircularProgress size={16} /> : <PlayArrowIcon />
              }
              onClick={() => setConfirmOpen(true)}
              disabled={running}
            >
              {t("pages.dsokBilling.history.runNow", {
                defaultValue: "Spustiť faktúráciu teraz",
              })}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchRuns}>
              {t("pages.dsokBilling.history.retry", { defaultValue: "Skúsiť" })}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("pages.dsokBilling.history.startedAt", {
                    defaultValue: "Spustené",
                  })}
                </TableCell>
                <TableCell>
                  {t("pages.dsokBilling.history.trigger", {
                    defaultValue: "Spustenie",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.processed", {
                    defaultValue: "Spracované",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.created", {
                    defaultValue: "Vytvorené",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.skipped", {
                    defaultValue: "Preskočené",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.errors", {
                    defaultValue: "Chyby",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.totalNet", {
                    defaultValue: "Bez DPH",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.vatBreakdown", {
                    defaultValue: "DPH (rozpis)",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokBilling.history.totalGross", {
                    defaultValue: "S DPH",
                  })}
                </TableCell>
                <TableCell align="right" width={80}>
                  {t("pages.dsokBilling.history.detail", {
                    defaultValue: "Detail",
                  })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t("pages.dsokBilling.history.noData", {
                        defaultValue: "Zatiaľ žiadne behy",
                      })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => (
                  <TableRow key={run._id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTimeSecondsSk(run.startedAt)}
                    </TableCell>
                    <TableCell>
                      {String(run.trigger || "").startsWith("manual")
                        ? t("pages.dsokBilling.history.triggerManual", {
                            defaultValue: "Ručne",
                          })
                        : t("pages.dsokBilling.history.triggerCron", {
                            defaultValue: "Automaticky",
                          })}
                    </TableCell>
                    <TableCell align="right">{run.processed ?? 0}</TableCell>
                    <TableCell align="right">{run.created ?? 0}</TableCell>
                    <TableCell align="right">{run.skipped ?? 0}</TableCell>
                    <TableCell align="right">
                      {run.errors > 0 ? (
                        <Chip label={run.errors} color="error" size="small" />
                      ) : (
                        0
                      )}
                    </TableCell>
                    {(() => {
                      const tot = getRunTotals(run);
                      return (
                        <>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {fmtEur(tot.net)}
                          </TableCell>
                          <TableCell align="right">
                            {tot.vatBreakdown && tot.vatBreakdown.length ? (
                              tot.vatBreakdown.map((v) => (
                                <Typography
                                  key={v.rate}
                                  variant="caption"
                                  sx={{ display: "block", whiteSpace: "nowrap" }}
                                >
                                  {v.rate} %: {fmtEur(v.vat)}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                          >
                            {fmtEur(tot.total)}
                          </TableCell>
                        </>
                      );
                    })()}
                    <TableCell align="right">
                      <Tooltip
                        title={t("pages.dsokBilling.history.viewDetail", {
                          defaultValue: "Zobraziť detail",
                        })}
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openDetail(run)}
                            disabled={
                              !Array.isArray(run.results) ||
                              run.results.length === 0
                            }
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Potvrdenie manuálneho spustenia */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t("pages.dsokBilling.history.confirmTitle", {
            defaultValue: "Spustiť faktúráciu teraz?",
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("pages.dsokBilling.history.confirmText", {
              defaultValue:
                "Spustí sa faktúračný cyklus pre všetkých zákazníkov s aktívnou faktúráciou. Vytvoria sa reálne faktúry v Abre.",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t("pages.dsokBilling.history.cancel", { defaultValue: "Zrušiť" })}
          </Button>
          <Button onClick={handleRunNow} variant="contained" color="primary">
            {t("pages.dsokBilling.history.runNow", {
              defaultValue: "Spustiť faktúráciu teraz",
            })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail behu — per-zákazník výsledky */}
      <Dialog
        open={!!detailRun}
        onClose={closeDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("pages.dsokBilling.history.runDetailTitle", {
            defaultValue: "Detail behu",
          })}
          {detailRun ? ` — ${formatDateTimeSecondsSk(detailRun.startedAt)}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Tooltip
                      title={t("pages.dsokBilling.history.selectAll", {
                        defaultValue: "Označiť všetky (neodoslané)",
                      })}
                    >
                      <span>
                        <Checkbox
                          size="small"
                          checked={allSelectableChecked}
                          indeterminate={someSelectableChecked}
                          onChange={toggleSelectAll}
                          disabled={
                            detailLoading || selectableKeys.length === 0
                          }
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.history.customer", {
                      defaultValue: "Zákazník",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.history.period", {
                      defaultValue: "Obdobie",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.history.status", {
                      defaultValue: "Stav",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.history.note", {
                      defaultValue: "Poznámka",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.dsokBilling.history.totalNet", {
                      defaultValue: "Bez DPH",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.dsokBilling.history.totalGross", {
                      defaultValue: "S DPH",
                    })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detailRun?.results || []).map((r, i) => {
                  const selectable = isRowSelectable(r);
                  const checkboxTitle = r.alreadySent
                    ? t("pages.dsokBilling.history.alreadySentTooltip", {
                        defaultValue: "Faktúra už bola odoslaná",
                      })
                    : r.status !== "created"
                      ? t("pages.dsokBilling.history.notSendableTooltip", {
                          defaultValue: "Faktúru nemožno odoslať",
                        })
                      : "";
                  return (
                  <TableRow key={`${r.customerId}-${i}`}>
                    <TableCell padding="checkbox">
                      <Tooltip title={checkboxTitle}>
                        <span>
                          <Checkbox
                            size="small"
                            checked={selectedKeys.has(resultKey(r))}
                            disabled={!selectable || detailLoading}
                            onChange={() => toggleRow(resultKey(r))}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {r.companyName || r.customerId}
                    </TableCell>
                    <TableCell>{r.periodKey || ""}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.status}
                        color={RESULT_STATUS_COLOR[r.status] || "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {r.invoiceNumber || r.reason || r.error || ""}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {r.totals ? fmtEur(r.totals.net) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {r.totals ? fmtEur(r.totals.total) : "—"}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Súhrn behu — celková suma bez DPH, rozpis DPH, s DPH. */}
          {(() => {
            const tot = getRunTotals(detailRun || {});
            const hasAny =
              (tot.vatBreakdown && tot.vatBreakdown.length) ||
              tot.net ||
              tot.total;
            if (!hasAny) return null;
            return (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Box sx={{ minWidth: 320 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 0.25 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.dsokBilling.history.totalNet", {
                        defaultValue: "Spolu bez DPH",
                      })}
                    </Typography>
                    <Typography variant="body2">{fmtEur(tot.net)}</Typography>
                  </Stack>
                  {(tot.vatBreakdown || []).map((v) => (
                    <Stack
                      key={v.rate}
                      direction="row"
                      justifyContent="space-between"
                      sx={{ py: 0.25 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("pages.dsokBilling.history.vatRate", {
                          defaultValue: "DPH",
                        })}{" "}
                        {v.rate} % ({fmtEur(v.base)})
                      </Typography>
                      <Typography variant="body2">{fmtEur(v.vat)}</Typography>
                    </Stack>
                  ))}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      py: 0.5,
                      mt: 0.5,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2">
                      {t("pages.dsokBilling.history.totalGross", {
                        defaultValue: "Spolu s DPH",
                      })}
                    </Typography>
                    <Typography variant="subtitle2">
                      {fmtEur(tot.total)}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Box>
            {detailRun?.emailsSentAt ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="caption" color="success.main">
                  {t("pages.dsokBilling.history.emailsSentAt", {
                    defaultValue: "Emaily odoslané",
                  })}
                  : {formatDateTimeSecondsSk(detailRun.emailsSentAt)}
                  {detailRun.emailSummary
                    ? ` (${t("pages.dsokBilling.history.sent", {
                        defaultValue: "úspešne",
                      })}: ${detailRun.emailSummary.sent ?? 0}, ${t(
                        "pages.dsokBilling.history.skipped",
                        { defaultValue: "preskočené" },
                      )}: ${detailRun.emailSummary.skipped ?? 0}, ${t(
                        "pages.dsokBilling.history.errors",
                        { defaultValue: "chyby" },
                      )}: ${detailRun.emailSummary.failed ?? 0})`
                    : ""}
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={
                    resendingFailed ? <CircularProgress size={16} /> : null
                  }
                  onClick={() => handleResendFailed(detailRun)}
                  disabled={resendingFailed}
                >
                  {t("pages.dsokBilling.history.resendFailed", {
                    defaultValue: "Poslať znova zlyhané",
                  })}
                </Button>
              </Stack>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={
                  sendingEmails ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PaidOutlinedIcon />
                  )
                }
                onClick={() => setSendEmailsConfirm(detailRun)}
                disabled={
                  sendingEmails ||
                  detailLoading ||
                  selectedKeys.size === 0
                }
              >
                {t("pages.dsokBilling.history.sendEmails", {
                  defaultValue: "Odoslať faktúry emailom",
                })}
                {selectedKeys.size > 0
                  ? ` (${selectedKeys.size})`
                  : ""}
              </Button>
            )}
          </Box>
          <Button onClick={closeDetail}>
            {t("pages.dsokBilling.history.close", { defaultValue: "Zavrieť" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test odoslania (dry-run) — výsledky za zákazníkov */}
      <Dialog
        open={!!dryRunResult}
        onClose={() => setDryRunResult(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("pages.dsokBilling.history.dryRunTitle", {
            defaultValue: "Test odoslania faktúr emailom (dry-run)",
          })}
        </DialogTitle>
        <DialogContent dividers>
          {dryRunResult && (
            <>
              <Alert
                severity={
                  dryRunResult.summary?.problems > 0 ? "warning" : "success"
                }
                sx={{ mb: 2 }}
              >
                {t("pages.dsokBilling.history.dryRunSummary", {
                  defaultValue: `Zákazníkov: ${dryRunResult.summary?.total ?? 0} · OK: ${
                    dryRunResult.summary?.ok ?? 0
                  } · Problém: ${dryRunResult.summary?.problems ?? 0}`,
                })}
                {dryRunResult.summary?.notifConfigured === false
                  ? " · Notif server NEnakonfigurovaný"
                  : ""}
                {dryRunResult.summary?.sendFlagEnabled === false
                  ? " · Odosielanie je vypnuté (flag)"
                  : ""}
              </Alert>
              <Typography variant="caption" color="text.secondary">
                {t("pages.dsokBilling.history.dryRunNote", {
                  defaultValue:
                    "Iba test pripravenosti — nič sa neodoslalo. Overený je príjemca a šablóna na notif serveri.",
                })}
              </Typography>
              <TableContainer sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("pages.dsokBilling.history.customer", {
                          defaultValue: "Zákazník",
                        })}
                      </TableCell>
                      <TableCell>
                        {t("pages.dsokBilling.history.email", {
                          defaultValue: "Email",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("pages.dsokBilling.history.status", {
                          defaultValue: "Stav",
                        })}
                      </TableCell>
                      <TableCell>
                        {t("pages.dsokBilling.history.problems", {
                          defaultValue: "Problémy",
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dryRunResult.results || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            {t("pages.dsokBilling.history.dryRunNoCustomers", {
                              defaultValue:
                                "Žiadny zákazník so zapnutou faktúráciou.",
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (dryRunResult.results || []).map((r) => (
                        <TableRow key={r.customerId}>
                          <TableCell>{r.companyName}</TableCell>
                          <TableCell>{r.billingEmail || "—"}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={r.status === "ok" ? "OK" : "Problém"}
                              color={r.status === "ok" ? "success" : "error"}
                            />
                          </TableCell>
                          <TableCell>
                            {r.issues && r.issues.length
                              ? r.issues
                                  .map((i) => dryRunIssueLabel(i))
                                  .join(", ")
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDryRunResult(null)}>
            {t("pages.dsokBilling.history.close", { defaultValue: "Zavrieť" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bezpečnostná otázka pred reálnym hromadným odoslaním faktúr emailom */}
      <Dialog
        open={!!sendEmailsConfirm}
        onClose={() => setSendEmailsConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("pages.dsokBilling.history.sendEmailsConfirmTitle", {
            defaultValue: "Odoslať faktúry emailom?",
          })}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t("pages.dsokBilling.history.sendEmailsConfirmWarn", {
              defaultValue:
                "Emaily sa zákazníkom odošlú NAOZAJ a dajú sa odoslať iba RAZ.",
            })}
          </Alert>
          <Typography variant="body2">
            {t("pages.dsokBilling.history.sendEmailsConfirmBody", {
              count: selectedKeys.size,
              defaultValue: `Naozaj odoslať označené faktúry (${selectedKeys.size}) emailom zákazníkom?`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEmailsConfirm(null)}>
            {t("pages.dsokBilling.history.cancel", { defaultValue: "Zrušiť" })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => doSendSelected(sendEmailsConfirm)}
          >
            {t("pages.dsokBilling.history.sendEmailsConfirm", {
              defaultValue: "Áno, odoslať",
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingHistoryPage;
